/**
 * What a cohort taught us about a question, in words rather than decimals.
 *
 * `0.32` is not a finding. "Adequate discrimination" is. Authors were reading
 * three numbers in the preview panel and doing the banding in their heads, and
 * the panel already owned every threshold needed to do it for them.
 *
 * **One rule set, three surfaces.** The same three checks were written out
 * three times: `QuestionListItem` computed `reviewReasons`, `assessment-details`
 * recomputed `needsReviewCount` inline for the section header chip, and the
 * preview panel had its own reuse warning. They had already drifted, the row
 * calling discrimination bad below `0.20` while `pbi-bands` calls it weak below
 * `0.25`, so the same question could be flagged on the row and read "Adequate"
 * in the panel. Every threshold now comes from the module that owns it:
 * `pbi-bands` for discrimination, `question-reuse` for exposure, and the
 * tagged-difficulty windows below.
 *
 * **A signal is a reading, not a verdict.** Two of the three are neutral most
 * of the time: a question that performed exactly as tagged with strong
 * discrimination still has both signals, they just say so approvingly. Only
 * `review` signals mean the author has something to fix, and that subset is
 * what the row triangle and the section count report.
 *
 * **A label is a summary, not a judgement.** The chips used to read
 * "Performed as tagged" and "Strong discrimination", which name the conclusion
 * and hide the finding: an author had to already know that Medium claims 45 to
 * 71% correct before "Performed as tagged" told them anything, and the phrase
 * is opaque to everyone else. Each label now carries the reading it is drawn
 * from, `Medium · 62% correct`, and the tint carries the judgement.
 */

import {
  dIndexBand,
  discriminationMethod,
  type DiscriminationMethodId,
} from "@/lib/discrimination"
import { pbiBand, type PbiBandKey } from "@/lib/pbi-bands"
import { questionReuse, reuseWarning } from "@/lib/question-reuse"

export type QuestionDifficultyTag = "Easy" | "Medium" | "Hard"

/**
 * Tagged difficulty to the p-value window it claims, share correct 0..1.
 *
 * The bands are the product's own vocabulary and the percentage is what item
 * analysis reports, so the two have to agree: a Hard question that 88% of the
 * cohort answered is a mislabelled question, and an author picking items off a
 * blueprint by difficulty is being misled by the tag.
 *
 * Note the direction. A *low* p-value means *few* got it right, so `Hard` sits
 * at the bottom of the range and measuring below a tag's floor means the item
 * played harder than advertised.
 */
export const TAGGED_DIFFICULTY_P_VALUE: Record<
  QuestionDifficultyTag,
  readonly [number, number]
> = {
  Easy: [0.72, 0.94],
  Medium: [0.45, 0.71],
  Hard: [0.18, 0.44],
}

export type QuestionSignalKey = "difficulty" | "discrimination" | "exposure"

/**
 * How the reading should be drawn, and whether it is a problem.
 *
 * `review` is the only one that means "act on this". `caution` is worth seeing
 * but is not a defect on its own: an item that played harder than its tag may
 * be tagged wrong or may be a good hard question, and only the author knows.
 */
export type QuestionSignalTone = "good" | "neutral" | "caution" | "review"

export interface QuestionSignal {
  key: QuestionSignalKey
  /** Chip text. Short enough to sit three across. */
  label: string
  tone: QuestionSignalTone
  /**
   * Font Awesome glyph name. Lives with the label rather than being derived
   * from the tone at each surface, so a signal cannot be drawn as a warning in
   * one place and a tick in another.
   */
  icon: string
  /**
   * One sentence saying what the label means for this question, shown under
   * the chips when the tone earns an explanation. `null` when the label is
   * self-explanatory and a sentence would just be the chip again.
   */
  detail: string | null
}

const WARNING_ICON = "fa-triangle-exclamation"
const GOOD_ICON = "fa-circle-check"

export interface QuestionSignalInput {
  /** Seeds a use count when the surface has no real one. */
  id: string
  /** What the author claimed. `null` or `""` when the item is untagged. */
  difficulty?: QuestionDifficultyTag | string | null
  /** Share who answered correctly, 0..1. `null` when never sat. */
  pValue: number | null
  pbi: number | null
  dIndex: number | null
  timesUsed?: number
  usedInSameCourse?: boolean
  neverUsed?: boolean
  /**
   * Which discrimination measure to read and name. Defaults to PBI, which is
   * what the row-level callers report; the preview panel passes the method the
   * author has chosen so the chip and the numbers under it agree.
   */
  /**
   * Exam-plan discrimination floor. When set, PBI below this value is a review
   * signal. When omitted, discrimination review uses the program default.
   */
  pbiReviewFloor?: number
}

/**
 * Exported because the adapters need the same answer.
 *
 * A surface that has to fall back to a displayable difficulty still has to be
 * able to say the fallback is not a tag, and it cannot do that if the test for
 * "is this a real tag" lives only in here.
 */
export function isDifficultyTag(
  value: unknown
): value is QuestionDifficultyTag {
  return value === "Easy" || value === "Medium" || value === "Hard"
}

/**
 * `65` and `0.65` both mean the same thing; surfaces store it both ways.
 *
 * `0` reads as absent rather than "nobody got it right", because that is what
 * it means in every row that has one: the paper surfaces default the field to
 * zero for questions with no exam history. A genuine wipeout would still be
 * carrying a cohort count, and those surfaces have no way to express it.
 */
export function toShareCorrect(value: number | null | undefined): number | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null
  if (value === 0) return null
  return value > 1 ? value / 100 : value
}

function difficultySignal(
  tag: QuestionDifficultyTag,
  pValue: number | null
): QuestionSignal {
  if (pValue === null) {
    return {
      key: "difficulty",
      label: `${tag} · never sat`,
      tone: "neutral",
      icon: "fa-gauge",
      detail: "No cohort has sat this, so the tag is still a claim.",
    }
  }

  const [low, high] = TAGGED_DIFFICULTY_P_VALUE[tag]
  const measured = Math.round(pValue * 100)
  // The tag the author set, beside what the cohort actually did with it. The
  // pair is the whole finding: neither half means much alone, and a reader who
  // has never seen the bands can still tell that 31% correct is not Easy.
  const label = `${tag} · ${measured}% correct`

  if (pValue < low) {
    return {
      key: "difficulty",
      label,
      tone: "caution",
      icon: WARNING_ICON,
      detail: `Tagged ${tag.toLowerCase()}, but only ${measured}% answered it correctly.`,
    }
  }

  if (pValue > high) {
    return {
      key: "difficulty",
      label,
      tone: "caution",
      icon: WARNING_ICON,
      detail: `Tagged ${tag.toLowerCase()}, but ${measured}% answered it correctly.`,
    }
  }

  return {
    key: "difficulty",
    label,
    tone: "good",
    icon: GOOD_ICON,
    // The green tint and the tick already say the tag held. A sentence here
    // would be the chip again in longer words.
    detail: null,
  }
}

const DISCRIMINATION_BAND_LABEL: Record<PbiBandKey, string> = {
  strong: "Separates well",
  adequate: "Separates somewhat",
  weak: "Doesn't separate",
  none: "No separation reading",
}

function discriminationSignal(
  pbi: number | null,
  dIndex: number | null,
  methodId: DiscriminationMethodId,
  pbiReviewFloor?: number,
): QuestionSignal {
  // Band the measure being named. PBI and D disagree at the margins, so
  // banding one and labelling the other is how a chip ends up reading
  // "Weak · D 0.42".
  const value = methodId === "index" ? dIndex : pbi
  const band = methodId === "index" ? dIndexBand(dIndex) : pbiBand(pbi)
  const { abbreviation } = discriminationMethod(methodId)
  const bandLabel =
    band.key === "none" ? DISCRIMINATION_BAND_LABEL.none : DISCRIMINATION_BAND_LABEL[band.key]
  const underExamPlanFloor =
    pbiReviewFloor !== undefined &&
    methodId !== "index" &&
    value !== null &&
    value < pbiReviewFloor
  const underProgramWeak =
    pbiReviewFloor === undefined &&
    methodId !== "index" &&
    band.key === "weak"

  return {
    key: "discrimination",
    label:
      band.key === "none" || value === null
        ? `No ${abbreviation} reading`
        : `${abbreviation} ${value.toFixed(2)}`,
    tone:
      underExamPlanFloor || underProgramWeak ? "review" : "neutral",
    icon:
      underExamPlanFloor || underProgramWeak ? WARNING_ICON : "fa-chart-simple",
    detail:
      band.key === "none" || value === null
        ? "This question has no exam history yet."
        : underExamPlanFloor || underProgramWeak
          ? `${bandLabel}. High and low scorers answered it about equally, so the item is not separating them.`
          : bandLabel,
  }
}

/** Tooltip / review copy: numeric label first, band name as supporting text. */
export function formatReviewReason(signal: QuestionSignal): string {
  if (signal.key === "discrimination" && signal.detail) {
    const band = signal.detail.split(".")[0]?.trim()
    if (band) return `${signal.label} · ${band}`
  }
  if (signal.detail) return `${signal.label}. ${signal.detail}`
  return signal.label
}

/**
 * Every reading the surface has, in the order an author asks for them: is it
 * the right level, does it do its job, and have these students seen it.
 *
 * Signals are omitted rather than faked. An untagged question has no difficulty
 * reading to give, and a question nobody has sat has no exposure to report.
 */
export function questionSignals(input: QuestionSignalInput): QuestionSignal[] {
  const signals: QuestionSignal[] = []

  if (isDifficultyTag(input.difficulty)) {
    signals.push(difficultySignal(input.difficulty, input.pValue))
  }

  signals.push(
    discriminationSignal(
      input.pbi,
      input.dIndex,
      input.methodId ?? "pbi",
      input.pbiReviewFloor,
    ),
  )

  const reuse = questionReuse({
    id: input.id,
    timesUsed: input.timesUsed,
    usedInSameCourse: input.usedInSameCourse,
    neverUsed: input.neverUsed,
    sat: input.pValue !== null,
  })

  if (reuse.overused) {
    signals.push({
      key: "exposure",
      label:
        reuse.reason === "same-course" ? "Seen by these students" : "Overused",
      tone: "review",
      icon: WARNING_ICON,
      detail: reuseWarning(reuse),
    })
  } else if (reuse.timesUsed > 0) {
    signals.push({
      key: "exposure",
      label:
        reuse.timesUsed === 1 ? "Used once" : `Used ${reuse.timesUsed} times`,
      tone: "neutral",
      icon: "fa-users",
      detail: null,
    })
  }

  return signals
}

/** The subset an author has to act on. Drives the row flag and the section count. */
export function questionReviewSignals(
  input: QuestionSignalInput
): QuestionSignal[] {
  return questionSignals(input).filter((signal) => signal.tone === "review")
}

export function questionNeedsReview(input: QuestionSignalInput): boolean {
  return questionReviewSignals(input).length > 0
}
